import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Zap, Trash2, Play, PauseCircle, TrendingUp, TrendingDown, History } from "lucide-react";
import HelpBubble from "../components/HelpBubble";

const FIELDS = [
  { v: "roi", label: "ROI (%)" },
  { v: "spend", label: "Spend ($)" },
  { v: "net_profit", label: "Net Profit ($)" },
  { v: "cpc", label: "CPC ($)" },
  { v: "clicks", label: "Clicks" },
  { v: "conversions", label: "Conversions" },
];
const OPS = ["<", "<=", ">", ">=", "=="];
const ACTIONS = [
  { v: "pause", label: "Pause campaign", icon: PauseCircle, color: "text-red-600" },
  { v: "activate", label: "Activate campaign", icon: Play, color: "text-emerald-600" },
  { v: "budget_up", label: "Increase budget %", icon: TrendingUp, color: "text-emerald-600" },
  { v: "budget_down", label: "Decrease budget %", icon: TrendingDown, color: "text-amber-600" },
  { v: "notify", label: "Notify only", icon: Zap, color: "text-blue-600" },
];

const PRESETS = [
  { name: "Kill dead campaigns", field: "roi", operator: "<", value: -20, action: "pause" },
  { name: "Scale winners +20%", field: "roi", operator: ">", value: 50, action: "budget_up", action_value: 20 },
  { name: "Cut budget on losers -30%", field: "roi", operator: "<", value: -10, action: "budget_down", action_value: 30 },
  { name: "Notify on high spend", field: "spend", operator: ">", value: 500, action: "notify" },
];

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [execs, setExecs] = useState([]);
  const [form, setForm] = useState({ name: "", field: "roi", operator: "<", value: -20, action: "pause", action_value: 20 });
  const [busy, setBusy] = useState(false);
  const [simulation, setSimulation] = useState(null);

  const load = async () => {
    try {
      const [{ data: r }, { data: e }] = await Promise.all([
        api.get("/rules"),
        api.get("/rules/executions?limit=20"),
      ]);
      setRules(r); setExecs(e);
    } catch (err) {
      console.error("rules load failed", err);
      toast.error(err?.response?.data?.detail || "Failed to load rules");
    }
  };
  useEffect(() => { load(); }, []);

  const createRule = async (preset) => {
    const data = preset || form;
    if (!data.field || !data.operator) return toast.error("Invalid rule");
    setBusy(true);
    try {
      await api.post("/rules", data);
      toast.success("Rule created");
      setForm({ name: "", field: "roi", operator: "<", value: -20, action: "pause", action_value: 20 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const simulate = async () => {
    if (!form.field || !form.operator) return toast.error("Fill rule first");
    setBusy(true);
    setSimulation(null);
    try {
      const { data } = await api.post("/rules/simulate", form);
      setSimulation(data);
      if (data.matched_count === 0) toast.info("This rule wouldn't affect any campaigns right now");
      else toast.success(`Would ${data.verb} ${data.matched_count} campaign(s)`);
    } catch {
      toast.error("Simulation failed");
    } finally { setBusy(false); }
  };

  const deleteRule = async (id) => {
    await api.delete(`/rules/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (r) => {
    await api.patch(`/rules/${r.rule_id}`, { enabled: !r.enabled });
    load();
  };

  const evalNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rules/evaluate-now");
      toast.success(`${data.executions.length} actions executed on ${data.evaluated_rows} campaigns`);
      load();
    } catch {
      toast.error("Eval failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-[1200px]" data-testid="rules-page" style={{ fontFamily: "Manrope" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-600" /> Auto-Rules <HelpBubble feature="rule_basics" />
          </h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            Runs every 30 min automatically · auto-pauses losers, scales winners
          </p>
        </div>
        <button
          onClick={evalNow}
          disabled={busy}
          data-testid="eval-now-btn"
          className="flex items-center gap-2 bg-blue-600 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
        >
          <Zap className="w-3.5 h-3.5" /> {busy ? "Running…" : "Run rules now"}
        </button>
      </div>

      {/* Presets */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Quick-add presets</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {PRESETS.map((p, i) => (
            <button
              key={p.name}
              onClick={() => createRule(p)}
              data-testid={`preset-${i}`}
              className="text-left bg-white border border-slate-200 hover:border-blue-400 rounded-md p-3 transition-colors"
            >
              <div className="text-xs text-slate-900 font-medium">{p.name}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-1">{p.field} {p.operator} {p.value} → {p.action}{p.action_value ? ` ${p.action_value}%` : ""}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom rule form */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Custom rule</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] text-slate-500 font-mono">Rule name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Kill dead finance campaigns"
              data-testid="rule-name"
              className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">If</label>
            <select
              value={form.field}
              onChange={(e) => setForm({ ...form, field: e.target.value })}
              data-testid="rule-field"
              className="block bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1 font-mono"
            >
              {FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">is</label>
            <select
              value={form.operator}
              onChange={(e) => setForm({ ...form, operator: e.target.value })}
              data-testid="rule-op"
              className="block bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1 font-mono"
            >
              {OPS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">Value</label>
            <input
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) })}
              data-testid="rule-value"
              className="block w-24 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1 font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">Then</label>
            <select
              value={form.action}
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              data-testid="rule-action"
              className="block bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1"
            >
              {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </select>
          </div>
          {(form.action === "budget_up" || form.action === "budget_down") && (
            <div>
              <label className="text-[10px] text-slate-500 font-mono">%</label>
              <input
                type="number"
                value={form.action_value}
                onChange={(e) => setForm({ ...form, action_value: parseFloat(e.target.value) })}
                className="block w-20 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs mt-1 font-mono"
              />
            </div>
          )}
          <button
            onClick={simulate}
            disabled={busy}
            data-testid="simulate-rule-btn"
            className="bg-white border border-slate-200 hover:border-blue-400 text-slate-900 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60"
          >
            {busy ? "…" : "Simulate"}
          </button>
          <button
            onClick={() => createRule()}
            disabled={busy}
            data-testid="create-rule-btn"
            className="bg-blue-600 text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            Create rule
          </button>
        </div>

        {simulation && (
          <div className="mt-4 bg-white border border-blue-500/30 rounded-md p-4" data-testid="simulation-preview">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-blue-700 font-mono">Simulation preview · no changes made</div>
              <button onClick={() => setSimulation(null)} className="text-slate-500 hover:text-slate-900 text-[11px]">dismiss</button>
            </div>
            <div className="text-sm text-slate-900 mb-3">
              Would <b>{simulation.verb}</b> <span className="text-blue-700">{simulation.matched_count} campaign(s)</span>
              {simulation.matched_count > 0 && (
                <> — currently <span className="font-mono">${simulation.total_spend.toLocaleString()}</span> spend, <span className="font-mono">${simulation.total_revenue.toLocaleString()}</span> revenue, <span className={`font-mono ${simulation.total_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>${simulation.total_profit.toLocaleString()}</span> profit</>
              )}
              {simulation.estimated_budget_delta !== 0 && (
                <> · est. daily spend change <span className={`font-mono ${simulation.estimated_budget_delta >= 0 ? "text-amber-700" : "text-emerald-600"}`}>${Math.abs(simulation.estimated_budget_delta).toFixed(2)}</span></>
              )}
            </div>
            {simulation.campaigns.length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded bg-white">
                <table className="w-full text-[11px]">
                  <thead className="text-[10px] uppercase text-slate-500 bg-white">
                    <tr><th className="text-left p-2">Campaign</th><th className="text-right p-2">{form.field}</th><th className="text-right p-2">Spend</th><th className="text-right p-2">Profit</th></tr>
                  </thead>
                  <tbody>
                    {simulation.campaigns.map((c, i) => (
                      <tr key={c.campaign_id || c.name || i} className="border-t border-slate-200" data-testid={`sim-row-${i}`}>
                        <td className="p-2 text-slate-900 truncate max-w-[220px]">{c.name}</td>
                        <td className="p-2 text-right font-mono text-slate-600">{typeof c.field_value === "number" ? c.field_value.toFixed(1) : c.field_value}</td>
                        <td className="p-2 text-right font-mono text-slate-500">${c.spend.toFixed(0)}</td>
                        <td className={`p-2 text-right font-mono ${c.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>${c.net_profit.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active rules */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Active rules ({rules.length})</div>
        <div className="space-y-2" data-testid="rules-list">
          {rules.map((r) => {
            const act = ACTIONS.find((a) => a.v === r.action);
            const Icon = act?.icon || Zap;
            return (
              <div key={r.rule_id} className="flex items-center gap-3 bg-white border border-slate-200 rounded p-2.5">
                <button onClick={() => toggle(r)} data-testid={`toggle-${r.rule_id}`} className={`w-2 h-2 rounded-full ${r.enabled ? "bg-emerald-400" : "bg-zinc-600"}`} />
                <Icon className={`w-4 h-4 ${act?.color || "text-slate-500"}`} />
                <div className="flex-1">
                  <div className="text-xs text-slate-900">{r.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.field} {r.operator} {r.value} → {r.action}{r.action_value && (r.action.includes("budget")) ? ` ${r.action_value}%` : ""}</div>
                </div>
                <button onClick={() => deleteRule(r.rule_id)} data-testid={`del-${r.rule_id}`} className="text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          {rules.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No rules yet. Add one from presets above.</div>}
        </div>
      </div>

      {/* Execution history */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-3.5 h-3.5 text-slate-500" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Recent executions</div>
        </div>
        <div className="space-y-1" data-testid="executions-list">
          {execs.map((e, i) => (
            <div key={`${e.at || ""}-${e.rule_name || ""}-${i}`} className="flex items-center gap-2 text-[11px] bg-white border border-slate-200 rounded px-2 py-1.5 font-mono">
              <span className="text-slate-500">{new Date(e.at).toLocaleString()}</span>
              <span className="text-slate-500">·</span>
              <span className="text-slate-900">{e.rule_name}</span>
              <span className="text-slate-500">→</span>
              <span className={`${e.executed ? "text-emerald-600" : "text-red-600"}`}>{e.action}</span>
              <span className="text-slate-500">on</span>
              <span className="text-slate-700">{e.campaign_name}</span>
              <span className="text-slate-500">({e.field}={e.value_seen})</span>
            </div>
          ))}
          {execs.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No executions yet.</div>}
        </div>
      </div>
    </div>
  );
}
